const bvCartRecoveryMarker = '_bv_cart_recovered';
const bvCartRecoveryNoteKey = 'bellvari-cart-recovery-note-v1';

function bvNavigateRefreshedCart() {
  try {
    const current = new URL(window.location.href);
    // One navigation per recovery. Never replay the failed quantity on arrival.
    if (current.searchParams.get(bvCartRecoveryMarker) === '1') return false;
    const root = window.Shopify?.routes?.root || '/';
    const cartPath = typeof routes !== 'undefined' && routes.cart_url || root + 'cart';
    const next = new URL(cartPath, current);
    if (next.origin !== current.origin || !/\/cart\/?$/.test(next.pathname)) return false;
    next.search = '';
    next.hash = '';
    for (const name of ['preview_theme_id', '_ab', '_fd', '_sc']) {
      if (current.searchParams.has(name)) next.searchParams.set(name, current.searchParams.get(name));
    }
    next.searchParams.set(bvCartRecoveryMarker, '1');
    next.searchParams.set('_bv_cart_sync', String(Date.now()));
    const note = document.querySelector('cart-note textarea');
    if (note && note.value !== note.defaultValue) {
      // Keep an unfinished order note out of the URL. If storage is unavailable,
      // retain the current page so the shopper can copy their note before refresh.
      const storage = window.sessionStorage;
      if (!storage) return false;
      storage.setItem(bvCartRecoveryNoteKey, JSON.stringify({ value: note.value, at: Date.now() }));
    }
    window.location.assign(next.href);
    return true;
  } catch (_) { return false; }
}

function bvShowCartRecoveryNotice() {
  try {
    if (new URL(window.location.href).searchParams.get(bvCartRecoveryMarker) !== '1') return;
  } catch (_) { return; }
  try {
    const saved = JSON.parse(window.sessionStorage?.getItem(bvCartRecoveryNoteKey) || 'null');
    if (saved && typeof saved.value === 'string' && Number.isFinite(saved.at) && Date.now() - saved.at >= 0 && Date.now() - saved.at < 120000) {
      const note = document.querySelector('cart-note textarea');
      if (note) { note.value = saved.value; note.dispatchEvent(new Event('input', { bubbles: true })); }
    }
    window.sessionStorage?.removeItem(bvCartRecoveryNoteKey);
  } catch (_) { /* The cart remains usable if optional note storage is unavailable. */ }
  const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
  if (errors) {
    errors.textContent = 'Your cart has been refreshed. Your last quantity change was not saved. Please enter it again.';
    errors.setAttribute?.('data-bv-cart-recovery-notice', 'true');
    errors.setAttribute?.('role', 'alert');
    errors.tabIndex = -1;
    errors.focus?.({ preventScroll: false });
  }
}

function bvClearCartRecoveryMarker() {
  for (const id of ['cart-errors', 'CartDrawer-CartErrors']) {
    const errors = document.getElementById(id);
    if (errors?.getAttribute?.('data-bv-cart-recovery-notice') === 'true') {
      errors.textContent = '';
      errors.removeAttribute('data-bv-cart-recovery-notice');
    }
  }
  try {
    const current = new URL(window.location.href);
    if (!current.searchParams.has(bvCartRecoveryMarker)) return;
    current.searchParams.delete(bvCartRecoveryMarker);
    current.searchParams.delete('_bv_cart_sync');
    window.history.replaceState(window.history.state, '', current.href);
  } catch (_) {}
}

// Checkout waits for both the quantity debounce and its cart request, so it cannot
// leave with the previous quantity while a visible change is still being saved.
const bvNativeCartPending = new Set();
function bvNativeCartBegin() {
  let settle;
  const pending = new Promise((resolve) => { settle = resolve; });
  bvNativeCartPending.add(pending);
  return (succeeded) => {
    bvNativeCartPending.delete(pending);
    settle(succeeded !== false);
  };
}
window.bvNativeCartSettled = async function () {
  if (!bvNativeCartPending.size) return;
  let timeout;
  try {
    await Promise.race([
      (async () => {
        while (bvNativeCartPending.size) {
          const results = await Promise.all([...bvNativeCartPending]);
          if (results.some((succeeded) => !succeeded)) {
            throw new Error('Your last cart change could not be saved. Please check the quantity and try checkout again.');
          }
        }
      })(),
      new Promise((_, reject) => {
        timeout = setTimeout(() => reject(new Error('Your cart is still updating. Please try checkout again.')), 15000);
      }),
    ]);
  } finally {
    clearTimeout(timeout);
  }
};

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const quantityChanges = new Map();
    this.addEventListener('change', (event) => {
      // Capture identity before the debounce: linked cart actions may remove
      // an earlier row and change every subsequent numeric line index.
      event.bvLineKey = event.target.closest?.('[data-bv-line-key]')?.dataset.bvLineKey || null;
      const key = event.bvLineKey || event.target;
      if (!quantityChanges.has(key)) {
        const complete = bvNativeCartBegin();
        quantityChanges.set(key, debounce(async (change) => {
          quantityChanges.delete(key);
          let succeeded = false;
          try {
            succeeded = (await this.onChange(change)) !== false;
          } finally {
            complete(succeeded);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER));
      }
      // Coalesce repeated edits to this line only. Another line must retain
      // its own pending change, which checkout also waits to finish.
      quantityChanges.get(key)(event);
    });
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
    if (!input) return;
    input.value = input.getAttribute('value');
    input.setCustomValidity('');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    // Restore the saved quantity and explain the error without leaving checkout invalid.
    event.target.setCustomValidity('');
    this.resetQuantityInput(index);
    this.updateLiveRegions(index, message);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = Number(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (event.target.value.trim() === '' || !Number.isSafeInteger(inputValue)) {
      message = window.cartStrings.error;
    } else if (inputValue !== 0 && inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      return this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement && document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    return this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const lineKey = event.bvLineKey || event.target.closest?.('[data-bv-line-key]')?.dataset.bvLineKey;
    if (!lineKey) {
      this.resetQuantityInput(line);
      this.updateLiveRegions(line, 'Your cart changed. Please refresh it and try again.');
      return Promise.resolve(false);
    }
    const complete = bvNativeCartBegin();
    let succeeded = false;
    const perform = () => this.saveQuantity(line, lineKey, quantity, event, name, variantId);
    const cart = window.BellvariEngravingCart;
    const result = typeof cart?.runNativeMutation === 'function' ? cart.runNativeMutation(perform) : Promise.resolve().then(perform);
    return Promise.resolve(result)
      .then((saved) => { succeeded = saved === true; return succeeded; })
      .catch(() => {
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        return false;
      })
      .finally(() => complete(succeeded));
  }

  saveQuantity(line, lineKey, quantity, event, name, variantId) {
    // The server receives a stable key. Never fall back to a row number when
    // an earlier mutation has changed cart ordering or removed this item.
    const currentRow = [...document.querySelectorAll('[data-bv-line-key]')].find(row => row.dataset.bvLineKey === lineKey);
    const currentInput = currentRow?.querySelector('input[name="updates[]"], input.quantity__input');
    if (currentInput?.dataset.index) line = currentInput.dataset.index;
    this.enableLoading(line);

    const body = JSON.stringify({
      id: lineKey,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const controller = new AbortController();
    const requestTimeout = setTimeout(() => controller.abort(), 20000);
    let succeeded = false;

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body, signal: controller.signal })
      .then((response) => {
        return response.text();
      })
      .then(async (state) => {
        const parsedState = JSON.parse(state);
        const responseError = parsedState.errors || (Number(parsedState.status) >= 400 && (parsedState.description || parsedState.message));
        if (typeof responseError === 'string' && /no valid id or line parameter/i.test(responseError)) {
          // Discounts can replace Shopify line keys between checkout and cart.
          // Refresh verified markup, but never replay a quantity against an
          // inferred variant or its former row number.
          let refreshed = false;
          try {
            if (typeof window.BellvariEngravingCart?.refresh === 'function') {
              await window.BellvariEngravingCart.refresh(false);
              refreshed = true;
            }
          } catch (_) { /* Keep the safe retry path when cart refresh also fails. */ }
          if (!refreshed && bvNavigateRefreshedCart()) return false;
          let message = refreshed
            ? 'Your cart has been refreshed. Check the quantities, then try your change again.'
            : 'Your cart changed. Refresh the cart, then try your quantity change again.';
          const note = document.querySelector('cart-note textarea');
          if (!refreshed && note && note.value !== note.defaultValue) {
            message = 'Your cart needs a refresh. Your order note is still here; copy it before refreshing, then try your quantity change again.';
          }
          const drawerActive = this.tagName === 'CART-DRAWER-ITEMS' || document.querySelector('cart-drawer.active');
          const errors = drawerActive
            ? document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors')
            : document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
          if (errors) {
            errors.textContent = message;
            errors.setAttribute?.('data-bv-cart-recovery-notice', 'true');
            if (!refreshed && typeof document.createElement === 'function') {
              const refreshButton = document.createElement('button');
              refreshButton.type = 'button';
              refreshButton.className = 'button button--secondary';
              refreshButton.textContent = 'Refresh cart';
              refreshButton.addEventListener('click', () => window.location.reload());
              errors.appendChild(refreshButton);
            }
          }
          else window.BellvariEngravingCart?.showError?.(message);
          return false;
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            currentInput || event.target;
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            this.resetQuantityInput(line);
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items.find(item => item.key === lineKey)?.quantity;
          let message = '';
          if (
            quantityElement &&
            items.length === parsedState.items.length &&
            updatedValue !== parseInt(quantityElement.value) &&
            !this.cartHasRequestedVariantTotal(quantityElement, quantity, parsedState)
          ) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        if (parsedState.errors) return false;
        bvClearCartRecoveryMarker();
        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        succeeded = !parsedState.errors;
        return succeeded;
      })
      .catch(() => {
        this.resetQuantityInput(line);
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        return false;
      })
      .finally(() => {
        clearTimeout(requestTimeout);
        this.disableLoading(line);
      });
  }

  // Automatic belt discounts can split one variant across paid and free lines.
  // Compare the resulting variant total before reporting a quantity shortfall.
  cartHasRequestedVariantTotal(quantityElement, quantity, parsedState) {
    const requestedTotal =
      Number(quantityElement.dataset.cartQuantity) -
      Number(quantityElement.getAttribute('value')) +
      Number(quantity);
    const variantTotal = parsedState.items
      .filter((item) => String(item.variant_id) === quantityElement.dataset.quantityVariantId)
      .reduce((total, item) => total + item.quantity, 0);
    return Number.isSafeInteger(requestedTotal) && variantTotal === requestedTotal;
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Restore an edited note only after cart-note has registered its save listener.
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bvShowCartRecoveryNotice, { once: true });
else bvShowCartRecoveryNotice();
